import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, GiftIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
interface PricingSectionProps {
  onCheckout?: () => void;
}
export function PricingSection({
  onCheckout
}: PricingSectionProps) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: 20
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  const mainIncludes = ['guía práctica paso a paso,', 'estrategias aplicadas de Inteligencia Artificial,', 'estructura para organizar tu oferta,', 'recursos de apoyo para acelerar el proceso,', 'materiales listos para ayudarte a pasar a la acción,', 'y una ruta diseñada para principiantes.'];
  const bonuses = [{
    title: 'Bono 1: Guía de activación rápida',
    desc: 'Una hoja de ruta pensada para ayudarte a comenzar sin sentirte paralizada por tanta información.'
  }, {
    title: 'Bono 2: Recursos listos para adaptar',
    desc: 'Materiales de apoyo que te permitirán organizar mejor tu oferta y acelerar tu proceso.'
  }, {
    title: 'Bono 3: Script de contacto para tus primeros clientes',
    desc: 'Una estructura simple para ayudarte a presentarte con más claridad y seguridad.'
  }];
  return <section className="bg-navy py-20 md:py-32 relative overflow-hidden" data-id="element-241">
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gold/10 rounded-full blur-[100px] pointer-events-none" data-id="element-242"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px] pointer-events-none" data-id="element-243"></div>

      <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-244">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-245">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-246">
            Todo esto es lo que recibes hoy al entrar a{' '}
            <span className="text-gold" data-id="element-247">IA Express 2026</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 items-start" data-id="element-248">
          {/* Main Offer Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} className="lg:col-span-7 bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-gold relative" data-id="element-249">
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold to-gold-light text-white px-6 py-2 rounded-full font-bold shadow-lg whitespace-nowrap" data-id="element-250">
              OFERTA ESPECIAL
            </div>

            <motion.div variants={itemVariants} className="text-center mb-8 pt-4" data-id="element-251">
              <h3 className="text-3xl font-serif font-bold text-navy mb-2" data-id="element-252">
                IA Express 2026
              </h3>
              <p className="text-slate-600 font-medium" data-id="element-253">
                Lanza tu mini-negocio visual desde tu celular en 48 horas
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-8" data-id="element-254">
              <p className="font-bold text-navy mb-4 text-lg" data-id="element-255">Recibes:</p>
              <ul className="space-y-3" data-id="element-256">
                {mainIncludes.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-257">
                    <CheckIcon className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" data-id="element-258" />
                    <span className="text-slate-700" data-id="element-259">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-8 pt-6 border-t border-slate-100" data-id="element-260">
              <p className="font-bold text-navy mb-4 text-lg flex items-center gap-2" data-id="element-261">
                <GiftIcon className="w-5 h-5 text-gold" data-id="element-262" />
                Además, también recibes bonos de apoyo:
              </p>
              <div className="space-y-4" data-id="element-263">
                {bonuses.map((bonus, index) => <div key={index} className="bg-cream p-4 rounded-xl border border-gold/20" data-id="element-264">
                    <p className="font-bold text-navy text-sm mb-1" data-id="element-265">
                      {bonus.title}
                    </p>
                    <p className="text-sm text-slate-600" data-id="element-266">{bonus.desc}</p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>

          {/* Pricing Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} className="lg:col-span-5 flex flex-col gap-6" data-id="element-267">
            <motion.div variants={itemVariants} className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 text-center" data-id="element-268">
              <p className="text-white text-lg mb-4" data-id="element-269">
                Hoy puedes acceder a todo esto por solo
              </p>

              <div className="flex justify-center items-center gap-4 mb-2" data-id="element-270">
                <span className="text-2xl text-slate-400 line-through decoration-rose-500 decoration-2" data-id="element-271">
                  $27 USD
                </span>
                <span className="bg-rose-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-272">
                  75% OFF
                </span>
              </div>

              <div className="text-6xl font-bold text-gold mb-6" data-id="element-273">
                $6.39{' '}
                <span className="text-2xl text-white font-normal" data-id="element-274">USD</span>
              </div>

              <p className="text-slate-300 text-sm mb-8" data-id="element-275">
                Un solo pago. Sin mensualidades. Sin complicaciones. Con acceso
                inmediato.
              </p>

              <CTAButton text="QUIERO ACCEDER AHORA" href="https://iaexpres2026.com/cart/46495237374142:1" className="w-full" data-id="element-276" />
            </motion.div>

            <motion.div variants={itemVariants} className="bg-cream rounded-3xl p-6 border border-gold/20" data-id="element-277">
              <p className="text-navy font-medium mb-2" data-id="element-278">
                Y la realidad es esta:
              </p>
              <p className="text-slate-700 text-sm leading-relaxed mb-4" data-id="element-279">
                Si este programa te ayuda a dar claridad a tu idea, estructurar
                tu primera oferta y acercarte a tu primer cliente, ya puede
                haberse pagado por sí mismo.
              </p>
              <p className="text-gold font-bold text-sm italic" data-id="element-280">
                No estás invirtiendo solo en información. Estás invirtiendo en
                una forma más práctica de empezar.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}